import React, { useId, useLayoutEffect, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useSearchParams } from "react-router-dom";

import { GoogleSignInButton } from "../../components/auth/GoogleSignInButton";
import { EmailRetentionPopoverSelect } from "../../components/onboarding/EmailRetentionPopoverSelect";
import Button from "../../components/ui/Button";
import Card from "../../components/ui/Card";
import {
  EMAIL_RETENTION_COLD_INBOUND_ONLY,
  normalizeEmailRetentionValue,
} from "../../config/emailRetentionOptions";
import { HELP_GOOGLE_ACCESS_URL } from "../../config/googleOAuthPublic";
import { mailboxService, mailboxesQueryKey } from "../../services/mailbox";
import { userProfileQueryKey, userProfileService } from "../../services/userProfile";
import { mailboxOAuthErrorIsAccountInUse, mailboxOAuthErrorMessage } from "../../utils/mailboxOAuthError";
import { persistMailboxHash } from "../../utils/mailboxScopeStorage";

const SETTINGS_RETURN_PATH = "/settings/mailboxes/";

/** Kebab menu: popover opens to the right of the trigger (CSS anchor positioning). */
function MailboxMoreMenu({
  mailboxHash,
  clearPending,
  onClearImported,
  disconnectLabel,
  disconnectConfirmMessage,
  onDisconnect,
  disconnectPending,
}) {
  const reactId = useId().replace(/:/g, "");
  const popoverId = `mailbox-more-${mailboxHash}-${reactId}`;
  const popoverRef = useRef(null);
  const anchorCssName = `--mailbox-more-${reactId}`;

  const handleDisconnect = () => {
    popoverRef.current?.hidePopover?.();
    const ok = window.confirm(disconnectConfirmMessage);
    if (!ok) return;
    onDisconnect(mailboxHash);
  };

  const handleDeleteEmails = () => {
    popoverRef.current?.hidePopover?.();
    const ok = window.confirm(
      "Delete all imported emails and leads from this mailbox? This cannot be undone.",
    );
    if (!ok) return;
    onClearImported(mailboxHash);
  };

  return (
    <>
      <button
        type="button"
        popoverTarget={popoverId}
        style={{ anchorName: anchorCssName }}
        className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded border border-transparent text-theme-muted transition hover:border-theme-border hover:text-theme-text"
        aria-label="More mailbox actions"
      >
        <span className="material-symbols-outlined text-[20px] leading-none" aria-hidden>
          more_vert
        </span>
      </button>
      <div
        ref={popoverRef}
        id={popoverId}
        popover="auto"
        style={{
          positionAnchor: anchorCssName,
          position: "fixed",
          left: "anchor(right)",
          top: "anchor(center)",
          marginLeft: "0.5rem",
          transform: "translateY(-50%)",
        }}
        className="z-50 min-w-[12rem] rounded-lg border border-theme-border bg-theme-panel p-1 shadow-lg"
      >
        <button
          type="button"
          onClick={handleDisconnect}
          disabled={disconnectPending}
          className="flex w-full rounded-md px-3 py-2 text-left font-mono text-[12px] text-theme-text transition hover:bg-theme-main disabled:cursor-not-allowed disabled:opacity-50"
        >
          {disconnectLabel}
        </button>
        <button
          type="button"
          onClick={handleDeleteEmails}
          disabled={clearPending}
          className="flex w-full rounded-md px-3 py-2 text-left font-mono text-[12px] text-theme-text transition hover:bg-theme-main disabled:cursor-not-allowed disabled:opacity-50"
        >
          Delete Emails
        </button>
      </div>
    </>
  );
}

const MESSAGE_RETENTION_INFO =
  "Choose how FlipMail retains imported email content, from full bodies for cold inbound mail only to metadata only. You can change this anytime.";

const SettingsMailboxes = () => {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [mailboxMessage, setMailboxMessage] = useState({ tone: "", text: "", oauthReason: "" });

  const { data: mailboxData } = useQuery({
    queryKey: mailboxesQueryKey,
    queryFn: mailboxService.list,
  });
  const mailboxes = mailboxData?.results || [];
  const connectedMailboxes = mailboxes.filter((m) => m.connected);
  const disconnectedMailboxes = mailboxes.filter((m) => !m.connected);

  useLayoutEffect(() => {
    const mailbox = searchParams.get("mailbox");
    if (!mailbox) return;
    const reason = searchParams.get("reason");
    const email = searchParams.get("email");
    const mailboxHashParam = searchParams.get("mailbox_hash") || "";
    const next = new URLSearchParams(searchParams);
    next.delete("mailbox");
    next.delete("reason");
    next.delete("email");
    next.delete("mailbox_hash");
    setSearchParams(next, { replace: true });

    if (mailbox === "connected") {
      if (mailboxHashParam) {
        persistMailboxHash(mailboxHashParam);
      }
      queryClient.invalidateQueries({ queryKey: mailboxesQueryKey });
      setMailboxMessage({ tone: "success", text: email ? `Connected ${email}.` : "Mailbox connected.", oauthReason: "" });
    } else if (mailbox === "error") {
      const r = (reason || "").trim();
      setMailboxMessage({
        tone: "error",
        text: mailboxOAuthErrorMessage(reason),
        oauthReason: r,
      });
    }
  }, [searchParams, setSearchParams, queryClient]);

  const startConnectMutation = useMutation({
    mutationFn: () => mailboxService.start({ return_path: SETTINGS_RETURN_PATH }),
    onSuccess: (data) => {
      window.location.href = data.auth_url;
    },
  });

  const runMailboxOAuth = () => {
    setMailboxMessage({ tone: "", text: "", oauthReason: "" });
    startConnectMutation.mutate();
  };

  const disconnectMutation = useMutation({
    mutationFn: (hash) => mailboxService.disconnect(hash),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: mailboxesQueryKey });
    },
  });

  const setPrimaryMutation = useMutation({
    mutationFn: (hash) => mailboxService.setPrimary(hash),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: mailboxesQueryKey });
    },
  });

  const clearImportedMutation = useMutation({
    mutationFn: (hash) => mailboxService.clearImported(hash),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: mailboxesQueryKey });
      queryClient.invalidateQueries({ queryKey: ["leads"] });
      queryClient.invalidateQueries({ queryKey: ["history-list"] });
      queryClient.invalidateQueries({ queryKey: ["analytics"] });
      queryClient.invalidateQueries({ queryKey: ["mailbox-audit-results"] });
      queryClient.invalidateQueries({ queryKey: ["bulk-email-tasks"] });
      setMailboxMessage({
        tone: "success",
        text: "Deleted imported emails and leads for this mailbox.",
        oauthReason: "",
      });
    },
    onError: () => {
      setMailboxMessage({
        tone: "error",
        text: "Could not delete imported data. Try again.",
        oauthReason: "",
      });
    },
  });

  const userProfileQuery = useQuery({
    queryKey: userProfileQueryKey,
    queryFn: userProfileService.get,
  });

  const retentionMutation = useMutation({
    mutationFn: (email_retention_policy) => userProfileService.update({ email_retention_policy }),
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: userProfileQueryKey });
    },
  });

  const emailRetentionPolicy = userProfileQuery.data
    ? normalizeEmailRetentionValue(userProfileQuery.data.email_retention_policy)
    : EMAIL_RETENTION_COLD_INBOUND_ONLY;

  let mailboxBannerClass = "";
  if (mailboxMessage.tone === "success") {
    mailboxBannerClass = "border-theme-accent/40 bg-theme-panel text-theme-accent";
  } else if (mailboxMessage.tone === "error") {
    if (mailboxOAuthErrorIsAccountInUse(mailboxMessage.oauthReason)) {
      mailboxBannerClass = "border-rose-500/30 bg-rose-500/10 text-theme-text";
    } else {
      mailboxBannerClass = "border-theme-border bg-theme-panel text-rose-400";
    }
  }

  const mailboxesPageHeader = (
    <>
      <h1 className="font-display font-bold italic text-[32px] md:text-[40px] leading-[1.1] text-theme-on-background">
        Mailboxes
      </h1>
      <p className="mt-2 max-w-2xl text-sm leading-relaxed text-theme-text opacity-80">
        Connect inboxes for scanning outreach and Gmail drafts. {" "}
        <a
          href={HELP_GOOGLE_ACCESS_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="text-theme-accent underline underline-offset-2 hover:opacity-90"
        >
          How we use Gmail data
        </a>.
      </p>
    </>
  );

  return (
    <div className="space-y-6">
      {mailboxMessage.text ? (
        <div className={`rounded border px-4 py-3 font-mono text-xs ${mailboxBannerClass}`} role="status">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
            <span>{mailboxMessage.text}</span>
            {mailboxMessage.tone === "success" ? (
              <Link
                to="/audit/new/"
                className="shrink-0 font-semibold underline underline-offset-2 decoration-current hover:opacity-80"
              >
                Run your first inbox audit
              </Link>
            ) : null}
          </div>
        </div>
      ) : null}

      <Card>
        <div className="space-y-2">{mailboxesPageHeader}</div>

        <div className="mt-8 space-y-3">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <h2 className="min-w-0 font-display font-bold text-lg text-theme-on-background">Gmail Accounts</h2>
            <GoogleSignInButton
              onClick={runMailboxOAuth}
              disabled={startConnectMutation.isPending}
              loading={startConnectMutation.isPending}
              label="Add Gmail inbox"
              size="compact"
              className="shrink-0"
            />
          </div>

          <div className="space-y-2">
            {connectedMailboxes.length === 0 && disconnectedMailboxes.length === 0 ? (
              <p className="font-mono text-[12px] text-theme-dim">No mailboxes connected yet.</p>
            ) : (
              connectedMailboxes.map((m) => (
                <div
                  key={m.hash}
                  className="flex flex-wrap items-center justify-between gap-3 rounded border border-theme-border bg-theme-main px-4 py-3"
                >
                  <div className="min-w-0">
                    <div className="text-sm text-theme-text truncate">{m.email}</div>
                    <div className="font-mono text-[11px] text-theme-dim">
                      {m.provider === "google" ? "Gmail" : m.provider}
                      {m.is_primary ? " · Primary" : ""}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {!m.is_primary ? (
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={() => setPrimaryMutation.mutate(m.hash)}
                        loading={setPrimaryMutation.isPending && setPrimaryMutation.variables === m.hash}
                      >
                        Set primary
                      </Button>
                    ) : null}
                    <MailboxMoreMenu
                      mailboxHash={m.hash}
                      clearPending={
                        clearImportedMutation.isPending && clearImportedMutation.variables === m.hash
                      }
                      disconnectPending={
                        disconnectMutation.isPending && disconnectMutation.variables === m.hash
                      }
                      disconnectLabel="Disconnect"
                      disconnectConfirmMessage="Are you sure? You'll lose any leads from this mailbox."
                      onDisconnect={(hash) => {
                        setMailboxMessage({ tone: "", text: "", oauthReason: "" });
                        disconnectMutation.mutate(hash);
                      }}
                      onClearImported={(hash) => {
                        setMailboxMessage({ tone: "", text: "", oauthReason: "" });
                        clearImportedMutation.mutate(hash);
                      }}
                    />
                  </div>
                </div>
              ))
            )}

            {disconnectedMailboxes.map((m) => (
              <div
                key={m.hash}
                className="flex flex-wrap items-center justify-between gap-3 rounded border border-theme-border bg-theme-main px-4 py-3"
              >
                <div className="min-w-0">
                  <div className="text-sm text-theme-text truncate">{m.email || "Disconnected mailbox"}</div>
                  <div className="font-mono text-[11px] text-theme-dim">Last error: {m.last_error || "unknown"}</div>
                </div>
                <div className="flex flex-wrap items-center justify-end gap-2">
                  <p className="font-mono text-[11px] text-theme-dim max-w-[220px] sm:mr-auto">
                    To reconnect, use <strong className="text-theme-text">Add Gmail inbox</strong> above.
                  </p>
                  <MailboxMoreMenu
                    mailboxHash={m.hash}
                    clearPending={
                      clearImportedMutation.isPending && clearImportedMutation.variables === m.hash
                    }
                    disconnectPending={
                      disconnectMutation.isPending && disconnectMutation.variables === m.hash
                    }
                    disconnectLabel="Remove"
                    disconnectConfirmMessage="Remove this mailbox entry from your account?"
                    onDisconnect={(hash) => {
                      setMailboxMessage({ tone: "", text: "", oauthReason: "" });
                      disconnectMutation.mutate(hash);
                    }}
                    onClearImported={(hash) => {
                      setMailboxMessage({ tone: "", text: "", oauthReason: "" });
                      clearImportedMutation.mutate(hash);
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="mt-8 max-w-md">
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <h2 className="font-display font-bold text-lg text-theme-on-background">Message Retention</h2>
            <button
              type="button"
              className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded border border-transparent text-theme-muted transition hover:border-theme-border hover:text-theme-text"
              title={MESSAGE_RETENTION_INFO}
              aria-label={MESSAGE_RETENTION_INFO}
            >
              <span className="material-symbols-outlined text-[20px] leading-none" aria-hidden>
                info
              </span>
            </button>
          </div>
          <EmailRetentionPopoverSelect
            hideLabel
            value={emailRetentionPolicy}
            onChange={(policy) => retentionMutation.mutate(policy)}
            disabled={userProfileQuery.isLoading || retentionMutation.isPending}
          />
        </div>
      </Card>
    </div>
  );
};

export default SettingsMailboxes;
